import { useViewRuntimeContext } from "@/components/common/view-runtime-context";
import { useCached3DObjectsIfReady } from "@/object-cache";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import {
  selectIElement,
  selectIElementsWorldPosition,
} from "@faro-lotv/app-component-toolbox";
import { assert, GUID } from "@faro-lotv/foundation";
import { isEqual } from "es-toolkit";
import {
  DragEvent as ReactDragEvent,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Raycaster, Vector2, Vector3 } from "three";
import { useCurrentScene } from "../mode-data-context";
import { selectOuterRoomSectionAncestor } from "../mode-selectors";

type ExistingWaypoint = {
  /** The id of the existing waypoint */
  existingWaypoint: GUID;
  /** The room section of the waypoint. Undefined for scans. */
  roomSection?: GUID;
  /** The position of the waypoint in pixels within the drag target */
  screenPosition?: Vector2;
};

type EmptyPositionOnSheet = {
  /** The world position on the sheet where a new waypoint can be created */
  worldPosition: Vector3;
};

/** Indicates what actions are possible for a drag event over a sheet. Returns undefined if no actions are possible. */
export type WaypointInfoForDragEvent =
  | ExistingWaypoint
  | EmptyPositionOnSheet
  | undefined;

/** Helper type to define the callback returned by useWaypointInfoForDragEvent */
export type GetWaypointInfoForDragEvent = (
  ev: ReactDragEvent<HTMLElement>,
) => WaypointInfoForDragEvent;

const WAYPOINT_HIT_RADIUS_PX = 40;

/**
 * @returns a callback to receive waypoint info below the mouse cursor for a given drag event.
 * The callback returns:
 * - either an existing waypoint, if the drag event happens close to one,
 * - a world position on the sheet, where a new waypoint can be created,
 * - or undefined, if no actions are possible.
 */
export function useWaypointInfoForDragEvent(): GetWaypointInfoForDragEvent {
  const { panos, activeSheets, locationMarkers, photos } = useCurrentScene();

  const waypointIds = useMemo(
    () => [
      ...panos.map((p) => p.id),
      ...locationMarkers.map((p) => p.id),
      ...photos.map((p) => p.id),
    ],
    [panos, locationMarkers, photos],
  );

  const sheets = useCached3DObjectsIfReady(
    activeSheets,
    "WaypointInfoForDragEvent",
  );
  const waypointPositions = useAppSelector(
    selectIElementsWorldPosition(waypointIds),
    isEqual,
  );

  const [rayCaster] = useState(() => new Raycaster());

  const { cameras } = useViewRuntimeContext();
  const activeCamera = cameras.at(0);

  const { getState } = useAppStore();

  return useCallback(
    (ev: ReactDragEvent<HTMLElement>): WaypointInfoForDragEvent => {
      assert(activeCamera, "Expected mode to have an active camera");

      const { left, top, width, height } = getOffsetInCurrentTarget(ev);

      const ndcMouse = new Vector2(
        (left / width) * 2 - 1,
        -(top / height) * 2 + 1,
      );

      let closestPano:
        | { distSq: number; id: GUID; screenPositionNdc: Vector3 }
        | undefined;
      for (let i = 0; i < waypointPositions.length; i++) {
        const waypointId = waypointIds[i];
        const panoPosition = waypointPositions[i];

        assert(waypointId && panoPosition, "Pano and position must be defined");

        const projectedPanoPosition = new Vector3()
          .fromArray(panoPosition)
          .project(activeCamera);

        const distSq =
          (ndcMouse.x - projectedPanoPosition.x) ** 2 +
          (ndcMouse.y - projectedPanoPosition.y) ** 2;

        if (!closestPano || distSq < closestPano.distSq) {
          closestPano = {
            distSq,
            id: waypointId,
            screenPositionNdc: projectedPanoPosition,
          };
        }
      }

      const maxWaypointDistSq = (WAYPOINT_HIT_RADIUS_PX / width) ** 2;

      if (closestPano && closestPano.distSq < maxWaypointDistSq) {
        const targetElement = selectIElement(closestPano.id)(getState());
        const roomSection =
          selectOuterRoomSectionAncestor(targetElement)(getState());

        return {
          existingWaypoint: closestPano.id,
          roomSection: roomSection?.id,
          screenPosition: new Vector2(
            ((closestPano.screenPositionNdc.x + 1) / 2) * width,
            ((1 - closestPano.screenPositionNdc.y) / 2) * height,
          ),
        };
      }

      // If no waypoint collision was found, raycast against sheets

      rayCaster.setFromCamera(ndcMouse, activeCamera);

      const sheetIntersections = rayCaster.intersectObjects(sheets);
      if (sheetIntersections.length > 0) {
        return {
          worldPosition: sheetIntersections[0].point,
        };
      }
    },
    [activeCamera, rayCaster, sheets, waypointPositions, waypointIds, getState],
  );
}

type DragEventOffset = {
  /** The distance to the left edge of the drag event target in pixels */
  left: number;
  /** The distance to the top edge of the drag event target in pixels */
  top: number;
  /** The total width of the drag event target in pixels */
  width: number;
  /** The total height of the drag event target in pixels */
  height: number;
};

/**
 * @returns the relative offset of a drag event and total size of the current event target.
 * @param ev The drag event to get the offset for.
 */
export function getOffsetInCurrentTarget(
  ev: ReactDragEvent<HTMLElement>,
): DragEventOffset {
  // The drop area is not necessarily the target, as children can also receive the event. Use the page coordinates to get the relative
  // position in the canvas instead.
  const bounds = ev.currentTarget.getBoundingClientRect();

  return {
    left: ev.nativeEvent.pageX - bounds.left,
    top: ev.nativeEvent.pageY - bounds.top,
    width: bounds.width,
    height: bounds.height,
  };
}
